import * as React from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'

import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { Experiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import useEndingSoonOfferList from 'hooks/EndingSoonOffer/useEndingSoonOfferList'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { useUserInteractions } from 'hooks/Recommendation/useUserInteractions'
import { useAppSelector } from 'hooks/reduxHooks'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { getCurrentUserId, selectRecDomainUserId } from 'selectors/accountSelectors'
import styled from 'styled-components'

const MIN_OFFERS_WITH_COUNTDOWN = 4
const ENDING_SOON_OFFER_LIMIT = 12
const RANKER_ENDING_SOON_OFFER_LIMIT = 30
const USER_INTERACTION_LOOKBACK_DAYS = 60

const CarouselContainer = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function useRankedEndingSoonOfferList(offerList: App.OfferList, options: { disabled: boolean }): App.OfferList {
  const leUserId = useAppSelector(getCurrentUserId)
  const domainUserId = useAppSelector(selectRecDomainUserId)
  const rankerOfferIds = offerList.offerIds.join(',')
  const filters = React.useMemo<App.OfferListFilters | undefined>(() => {
    if (!rankerOfferIds) {
      return undefined
    }

    return {
      lere: 'ranker',
      leUserId,
      domainUserId,
      rankerOfferIds,
    }
  }, [domainUserId, leUserId, rankerOfferIds])

  return useOfferList(filters, { disabled: options.disabled || !filters })
}

function getSelectedEndingSoonOfferList(offerList: App.OfferList, rankedOfferList: App.OfferList, shouldUseRankedOfferList: boolean): App.OfferList {
  if (shouldUseRankedOfferList) {
    return rankedOfferList
  }

  return offerList
}

function OfferEndingSoonCarousel() {
  const intl = useIntl()
  const endingSoonOfferList = useEndingSoonOfferList({ limit: ENDING_SOON_OFFER_LIMIT })
  const hasEnoughOffers = endingSoonOfferList.offerIds.length >= MIN_OFFERS_WITH_COUNTDOWN
  const hasResolvedBaseOfferList = !endingSoonOfferList.fetching && hasEnoughOffers
  const [carouselInViewRef, carouselInView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })
  const [userInteractionsInLookbackWindow, userInteractionsInLookbackWindowFetching] = useUserInteractions({
    interactionType: 'all',
    sinceDaysAgo: USER_INTERACTION_LOOKBACK_DAYS,
    disabled: !hasResolvedBaseOfferList,
  })
  const hasUserInteractionHistory = userInteractionsInLookbackWindow.length > 0
  const hasResolvedExperimentEligibility = !userInteractionsInLookbackWindowFetching
  const canActivateExperiment = carouselInView &&
    hasEnoughOffers &&
    hasResolvedExperimentEligibility &&
    hasUserInteractionHistory
  const { variation: rankerTestVariant } = useExperiment(
    Experiments.lereEndingSoonRanker,
    { canActivate: canActivateExperiment },
  )
  const shouldUseRankerList = rankerTestVariant === 'on'
  const rankerDisabled = !hasResolvedBaseOfferList || !shouldUseRankerList || !hasResolvedExperimentEligibility || !hasUserInteractionHistory
  const rankerCandidateOfferList = useEndingSoonOfferList({
    disabled: rankerDisabled,
    limit: RANKER_ENDING_SOON_OFFER_LIMIT,
  })
  const rankedEndingSoonOfferList = useRankedEndingSoonOfferList(rankerCandidateOfferList, {
    disabled: rankerDisabled || rankerCandidateOfferList.fetching || !rankerCandidateOfferList.offerIds.length,
  })
  const shouldUseRankedOfferList = shouldUseRankerList &&
    hasUserInteractionHistory &&
    !rankedEndingSoonOfferList.fetching &&
    !rankedEndingSoonOfferList.error &&
    rankedEndingSoonOfferList.offerIds.length >= MIN_OFFERS_WITH_COUNTDOWN
  const selectedOfferList = getSelectedEndingSoonOfferList(
    endingSoonOfferList,
    rankedEndingSoonOfferList,
    shouldUseRankedOfferList,
  )

  const title = intl.formatMessage(
    {
      id: 'Yw4KFRYfjiFEmE4VsWwK',
      defaultMessage: 'Offers <i>ending</i> soon',
      description: 'Title for offers ending soon carousel with italicized "ending"',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  // TODO: Move visibility decision to SSR to avoid CLS (Cumulative Layout Shift)
  // Currently CSR-only due to stream API limitation. Once unified search API supports
  // ending soon sorting, we can prefetch data server-side and conditionally render.

  // Don't render while loading or if there aren't enough offers
  if (endingSoonOfferList.fetching || !hasEnoughOffers) {
    return null
  }

  return (
    <CarouselContainer ref={carouselInViewRef}>
      <VerticalSpacer gap={24}>
        <LayoutContainer>
          <Heading variant="heading2">{title}</Heading>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={selectedOfferList}
          limit={ENDING_SOON_OFFER_LIMIT}
          tabletLimit={ENDING_SOON_OFFER_LIMIT}
          tileStyle="card-default"
          tileSize="medium"
        />
      </VerticalSpacer>
    </CarouselContainer>
  )
}

export default OfferEndingSoonCarousel
